import { skipToken, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import type { BandQuestsResponse, CreateBandTicketInvoice, InvestBandRaffleTickets } from '../services/generated/Api';
import { useAuthedQuery } from './useAuthedQuery';

export function useBandStatus() {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.bandStatus,
    queryFn: () => apiClient.api.bandControllerGetStatus(),
    refetchInterval: 60_000,
  });
}

export function useBandQuests() {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.bandQuests,
    queryFn: () => apiClient.api.bandControllerGetQuests(),
    refetchInterval: 60_000,
  });
}

export function useBandFreeCases() {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.bandFreeCases,
    queryFn: () => apiClient.api.bandControllerGetFreeCases(),
    refetchInterval: 60_000,
  });
}

export function useBandMembersPreview(enabled: boolean) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.bandMembersPreview,
    queryFn: () => apiClient.api.bandControllerGetMembersPreview(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useBandRaffleRound() {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.bandRaffleRound,
    queryFn: () => apiClient.api.bandRaffleControllerGetRound(),
    refetchInterval: 60_000,
  });
}

export function useBandRaffleRoundDetail(roundId: string | null) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.bandRaffleRoundDetail(roundId),
    queryFn: roundId ? () => apiClient.api.bandRaffleControllerGetRoundDetail(roundId) : skipToken,
    refetchInterval: 60_000,
  });
}

export function useBandRaffleWinners() {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.bandRaffleWinners,
    queryFn: () => apiClient.api.bandRaffleControllerGetWinners(),
  });
}

export function useClaimBandQuest() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (questId: string) => apiClient.api.bandControllerClaimQuest(questId),
    onSuccess: (_data, questId) => {
      queryClient.setQueryData<BandQuestsResponse>(queryKeys.bandQuests, (old) =>
        old
          ? { ...old, quests: old.quests.map((q) => (q.questId === questId ? { ...q, claimed: true } : q)) }
          : old,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.bandQuests });
      queryClient.invalidateQueries({ queryKey: queryKeys.bandStatus });
    },
  });
}

export function useClaimBandCashback() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.api.bandControllerClaimCashback(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bandStatus });
    },
  });
}

export function useInvestBandRaffle() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roundId, tickets }: { roundId: string; tickets: InvestBandRaffleTickets['tickets'] }) =>
      apiClient.api.bandRaffleControllerInvest(roundId, { tickets }),
    onSuccess: (_data, { roundId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bandRaffleRound });
      queryClient.invalidateQueries({ queryKey: queryKeys.bandRaffleRoundDetail(roundId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.bandStatus });
    },
  });
}

export function useCreateBandTicketInvoice() {
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: (data: CreateBandTicketInvoice) => apiClient.api.bandControllerCreateTicketInvoice(data),
  });
}

export function usePayBandTicketWithClickCard() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) => apiClient.api.bandControllerPayTicketWithClickCard({ cardId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bandStatus });
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}
